<script setup lang="ts">
import { AppText } from '@shared/ui/typography'
import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'
import { LoaderOverlay } from '@shared/ui/loaders'
import { AppBadge } from '@shared/ui/badges'

import { useTableAssignmentTemplates } from '../../../model/assignments/table-assignment-templates.hook'
import AssignmentTemplateActions from './AssignmentTemplateActions.vue'
import AssignmentTemplateCover from './AssignmentTemplateCover.vue'
import AssignmentTemplateTaxonomy from './AssignmentTemplateTaxonomy.vue'

defineOptions({
  name: 'TableAssignmentTemplates',
})

const {
  refTemplates,
  refIsLoading,
  refHasTemplates,
  refIsEmpty,
  refHasFilters,
  refMeta,
  refPerPageOptions,
  formatDateTime,
  getTypeLabel,
  getDisciplineLabel,
  getGrade,
  getStatusLabel,
  getStatusColor,
  openTemplate,
  setChangePage,
  setChangePerPage,
} = useTableAssignmentTemplates()
</script>

<template>
  <div class="table-assignment-templates">
    <AppTable
      class="rounded-table"
      :meta="refMeta"
      :per-page-options="refPerPageOptions"
      :loading="refIsLoading"
      @change:page="setChangePage"
      @change:per-page="setChangePerPage"
    >
      <template #head>
        <AppTr>
          <AppTh class="text-center table-assignment-templates__id-cell">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              #
            </AppText>
          </AppTh>
          <AppTh class="table-assignment-templates__cover-cell">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Cover
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Title
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Type
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Discipline
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Grade
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Curriculum
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Status
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Updated
            </AppText>
          </AppTh>
          <AppTh class="table-assignment-templates__actions-cell" />
        </AppTr>
      </template>

      <template
        v-if="refHasTemplates"
        #body
      >
        <AppTr
          v-for="template in refTemplates"
          :key="template.id"
        >
          <AppTd class="medium text-center table-assignment-templates__id-cell">
            <button
              type="button"
              class="table-assignment-templates__cell-link"
              :aria-label="`Open ${template.title}`"
              @click="openTemplate(template)"
            >
              <AppText
                name="body1"
                color="blue-600"
              >
                {{ template.id }}
              </AppText>
            </button>
          </AppTd>
          <AppTd class="medium table-assignment-templates__cover-cell">
            <AssignmentTemplateCover :template="template" />
          </AppTd>
          <AppTd class="medium">
            <button
              type="button"
              class="table-assignment-templates__cell-link"
              :aria-label="`Open ${template.title}`"
              @click="openTemplate(template)"
            >
              <AppText
                name="body1"
                color="blue-600"
              >
                {{ template.title }}
              </AppText>
            </button>
          </AppTd>
          <AppTd class="medium">
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ getTypeLabel(template) }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ getDisciplineLabel(template) }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ getGrade(template) }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <AssignmentTemplateTaxonomy :template="template" />
          </AppTd>
          <AppTd class="medium">
            <AppBadge
              :color="getStatusColor(template)"
              :text="getStatusLabel(template)"
            />
          </AppTd>
          <AppTd class="medium table-assignment-templates__date-cell">
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ formatDateTime(template.updated_at) }}
            </AppText>
          </AppTd>
          <AppTd class="zero-padding table-assignment-templates__actions-cell">
            <AssignmentTemplateActions :template="template" />
          </AppTd>
        </AppTr>
      </template>
    </AppTable>

    <div
      v-if="refIsEmpty"
      class="table-assignment-templates__empty"
    >
      <AppText
        name="body2"
        color="slate-500"
      >
        {{ refHasFilters
          ? 'No assignment templates match these filters.'
          : 'No assignment templates yet.' }}
      </AppText>
    </div>

    <LoaderOverlay v-if="refIsLoading" />
  </div>
</template>

<style scoped lang="scss" src="./TableAssignmentTemplates.scss" />
